import React, { useMemo } from 'react';
import CollectionList from '@/se/components/collection/CollectionList';
import { subscription } from '@/graph/physicians';
import { sortDate, sortNumber, sortString } from '@/util/sort';
import { TruncatedText } from '@/se/components/typography';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import { ArrowBack, KeyboardArrowRight } from '@material-ui/icons';
import { addDays, format, getMonth, getYear, isThisYear, isWeekend, parse } from 'date-fns';
import { physicianStats } from '@/graph/bookings';
import { roundFixed } from '../sensor/SensorList';
import { Subscription } from '@apollo/client/react/components';
import { MetricTooltip } from '@/components/pages/blockSchedule/metrics/MetricTooltip';
import { exportPhysicianCSV, exportPhysicianXLSX, exportPhysicianPDF } from './physicianExport';

const RingWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
`;

const Pie = styled.div`
  width: ${props => props.size || 18}px;
  height: ${props => props.size || 18}px;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  background: ${props => props.$background || 'transparent'};
`;

const MiniPie = ({ percent, color, size }) => {
  const p = Math.max(0, Math.min(100, Number(percent) || 0));
  const bg = 'rgba(255,255,255,0.12)';
  const gradient = `conic-gradient(${color} 0% ${p}%, ${bg} ${p}% 100%)`;
  return <Pie size={size} $background={gradient} />;
};

const PercentText = styled.span`
  font-weight: 800;
  font-size: ${props => (props.$fontSize ? `${props.$fontSize}px` : '14px')};
`;

const HoursText = styled.span`
  margin-left: 10px;
  opacity: 0.95;
  font-size: ${props => (props.$fontSize ? `${props.$fontSize}px` : '13px')};
`;

const PercentageRingCell = ({ percentage, color, hours }) => {
  const wrapperRef = React.useRef();
  const [size, setSize] = React.useState(22);

  React.useEffect(() => {
    const el = wrapperRef.current;
    const td = el?.closest && el.closest('td');
    const recompute = () => {
      const h = (td && td.clientHeight) || (el && el.clientHeight) || 22;
      const next = Math.max(18, Math.min(32, h - 6));
      setSize(next);
    };
    recompute();
    if (typeof ResizeObserver !== 'undefined' && td) {
      const ro = new ResizeObserver(recompute);
      ro.observe(td);
      return () => ro.disconnect();
    } else {
      window.addEventListener('resize', recompute);
      return () => window.removeEventListener('resize', recompute);
    }
  }, []);

  const percentFontSize = Math.max(13, Math.min(18, Math.round(size * 0.65)));
  const hoursFontSize = Math.max(12, Math.min(17, Math.round(size * 0.6)));

  return (
    <RingWrapper ref={wrapperRef}>
      <MiniPie percent={percentage} color={color} size={size} />
      <PercentText $fontSize={percentFontSize}>{Math.round(Number(percentage) || 0)}%</PercentText>
      {hours !== undefined && <HoursText $fontSize={hoursFontSize}>{roundFixed(hours)}h</HoursText>}
    </RingWrapper>
  );
};

const BarOuter = styled.div`
  position: relative;
  width: 160px;
  height: 16px;
  background: transparent;
  border-radius: 6px;
  overflow: hidden;
`;

const BarAllocated = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: ${props => Math.max(0, Math.min(100, props.percent || 0))}%;
  background: rgba(43, 60, 135, 0.6);
`;

const BarScheduled = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: ${props => Math.max(0, Math.min(100, props.percent || 0))}%;
  background: #00a7f7;
`;

const BarUsed = styled.div`
  position: absolute;
  left: 0;
  width: ${props => Math.max(0, Math.min(100, props.percent || 0))}%;
  height: 55%;
  top: 22.5%;
  background: #99e6b3;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25) inset;
  border-radius: 4px;
`;

const CapacityBar = ({ usedPct, scheduledPct }) => {
  const allocated = 100;
  const scheduled = Math.min(Math.max(0, scheduledPct || 0), allocated);
  const used = Math.min(Math.max(0, usedPct || 0), allocated);
  return (
    <BarOuter>
      <BarAllocated percent={allocated} />
      <BarScheduled percent={scheduled} />
      <BarUsed percent={used} />
    </BarOuter>
  );
};

const ViewAffordance = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  color: #00a7f7;
  font-weight: 700;
  letter-spacing: 0.2px;
  opacity: 0.95;
`;

const listColumns = [
  {
    title: 'Active Physicians',
    lens: data => data?.physician?.name,
    Component: props => <TruncatedText>{props.data}</TruncatedText>,
    sortFunction: (l, r) => sortString(l?.physician?.name, r?.physician?.name),
  },
  {
    title: 'Speciality',
    lens: data => data?.physician?.speciality,
    Component: props => <TruncatedText>{props.data}</TruncatedText>,
    sortFunction: (l, r) => sortString(l?.physician?.speciality, r?.physician?.speciality),
  },
  {
    title: 'Allocated',
    key: 'percentage',
    lens: data => ({
      percentage: data?.stat?.bookedPercentage ?? 0,
      hours: data?.stat?.bookedHours ?? 0,
    }),
    Component: props => (
      <PercentageRingCell percentage={props.data?.percentage} hours={props.data?.hours} color="#3D4A7A" />
    ),
    sortFunction: (l, r) => sortNumber(l?.stat?.bookedPercentage, r?.stat?.bookedPercentage),
    InfoComponent: () => (
      <MetricTooltip
        title="Allocated %"
        description="The percentage of total OR capacity assigned to this physician. (= Allocated Block ÷ Total Capacity × 100)"
      />
    ),
  },
  {
    title: 'Scheduled',
    key: 'percentage',
    lens: data => ({
      percentage: data?.stat?.relativeUtilization ?? 0,
      hours: data?.stat?.utilizedHours ?? 0,
    }),
    Component: props => (
      <PercentageRingCell percentage={props.data?.percentage} hours={props.data?.hours} color="#00a7f7" />
    ),
    sortFunction: (l, r) => sortNumber(l?.stat?.relativeUtilization, r?.stat?.relativeUtilization),
    InfoComponent: () => (
      <MetricTooltip
        title="Scheduled %"
        description="The percentage of this physician’s allocated time that has cases scheduled. (= Time for Cases Scheduled ÷ Allocated Block × 100)"
      />
    ),
  },
  {
    title: 'Used',
    key: 'percentage',
    lens: data => ({
      percentage: data?.stat?.actualUtilization ?? 0,
      hours: data?.stat?.actualUtilizedHours ?? 0,
    }),
    Component: props => (
      <PercentageRingCell percentage={props.data?.percentage} hours={props.data?.hours} color="#99E6B3" />
    ),
    sortFunction: (l, r) => sortNumber(l?.stat?.actualUtilization, r?.stat?.actualUtilization),
    InfoComponent: () => (
      <MetricTooltip
        title="Used %"
        description="The percentage of this physician’s allocated time actually used for surgeries (measured wheels in → wheels out). (= Wheels Time ÷ Allocated Block × 100)"
      />
    ),
  },
  {
    title: '',
    lens: data => ({
      scheduledPct: data?.stat?.relativeUtilization ?? 0,
      usedPct: data?.stat?.actualUtilization ?? 0,
    }),
    Component: ({ data }) => <CapacityBar usedPct={data?.usedPct || 0} scheduledPct={data?.scheduledPct || 0} />,
    sortDisabled: true,
  },
  {
    title: '',
    lens: () => true,
    Component: () => (
      <ViewAffordance>
        <span>View</span>
        <KeyboardArrowRight fontSize="small" />
      </ViewAffordance>
    ),
    sortDisabled: true,
  },
];

const viewColumns = [
  {
    title: 'Month',
    lens: data => data?.label,
    Component: props => <TruncatedText>{props.data}</TruncatedText>,
    sortFunction: (l, r) => sortDate(l?.date, r?.date),
  },
  {
    title: 'Allocated',
    key: 'percentage',
    lens: data => ({
      percentage: data?.physicianBookedUtilization ?? 0,
      hours: data?.physicianBookedHours ?? 0,
    }),
    Component: props => (
      <PercentageRingCell percentage={props.data?.percentage} hours={props.data?.hours} color="#3D4A7A" />
    ),
    sortFunction: (l, r) => sortNumber(l?.physicianBookedUtilization, r?.physicianBookedUtilization),
    InfoComponent: () => (
      <MetricTooltip
        title="Allocated %"
        description="The percentage of total OR capacity assigned to this physician. (= Allocated Block ÷ Total Capacity × 100)"
      />
    ),
  },
  {
    title: 'Scheduled',
    key: 'percentage',
    lens: data => ({
      percentage: data?.physicianRelativeUtilization ?? 0,
      hours: data?.physicianScheduledHours ?? 0,
    }),
    Component: props => (
      <PercentageRingCell percentage={props.data?.percentage} hours={props.data?.hours} color="#00a7f7" />
    ),
    sortFunction: (l, r) => sortNumber(l?.physicianRelativeUtilization, r?.physicianRelativeUtilization),
    InfoComponent: () => (
      <MetricTooltip
        title="Scheduled %"
        description="The percentage of this physician’s allocated time that has cases scheduled. (= Time for Cases Scheduled ÷ Allocated Block × 100)"
      />
    ),
  },
  {
    title: 'Used',
    key: 'percentage',
    lens: data => ({
      percentage: data?.physicianActualUtilization ?? 0,
      hours: data?.physicianActualHours ?? 0,
    }),
    Component: props => (
      <PercentageRingCell percentage={props.data?.percentage} hours={props.data?.hours} color="#99E6B3" />
    ),
    sortFunction: (l, r) => sortNumber(l?.physicianActualUtilization, r?.physicianActualUtilization),
    InfoComponent: () => (
      <MetricTooltip
        title="Used %"
        description="The percentage of this physician’s allocated time actually used for surgeries (measured wheels in → wheels out). (= Wheels Time ÷ Allocated Block × 100)"
      />
    ),
  },
  {
    title: '',
    lens: data => ({
      scheduledPct: data?.physicianRelativeUtilization ?? 0,
      usedPct: data?.physicianActualUtilization ?? 0,
    }),
    Component: ({ data }) => <CapacityBar usedPct={data?.usedPct || 0} scheduledPct={data?.scheduledPct || 0} />,
    sortDisabled: true,
  },
];

const normalizeDateKey = dateStr => {
  if (!dateStr) return '';
  try {
    // Use a stable key so we can align distributions by date
    return format(parse(dateStr), 'YYYY-MM-DD');
  } catch (e) {
    // Fall back to the raw server value if parsing fails
    return String(dateStr);
  }
};

const median = values => {
  if (!values || values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

const localDateToJsDate = localDate => {
  if (!localDate) return null;
  try {
    // js-joda LocalDate: year(), monthValue(), dayOfMonth()
    return new Date(localDate.year(), localDate.monthValue() - 1, localDate.dayOfMonth());
  } catch (e) {
    return null;
  }
};

const buildDailyPoints = (statistics, dateRange) => {
  const stats = statistics ?? [];
  const statIds = stats.map(s => s?.id).filter(Boolean);

  const byStat = {};
  let minTime = null;
  let maxTime = null;

  stats.forEach(stat => {
    const id = stat?.id;
    if (!id) return;

    const map = new Map();
    (stat?.distribution ?? []).forEach(p => {
      const key = normalizeDateKey(p?.date);
      if (!key) return;
      map.set(key, p?.value ?? 0);

      const t = parse(key).getTime();
      if (Number.isFinite(t)) {
        minTime = minTime === null ? t : Math.min(minTime, t);
        maxTime = maxTime === null ? t : Math.max(maxTime, t);
      }
    });

    byStat[id] = map;
  });

  // Prefer the explicit UI date range if provided (so we include no-booking days too).
  const fromDate = localDateToJsDate(dateRange?.from);
  const toDate = localDateToJsDate(dateRange?.to);
  if (fromDate && toDate) {
    minTime = fromDate.getTime();
    maxTime = toDate.getTime();
  }

  if (minTime === null || maxTime === null) return [];

  const points = [];
  for (let d = new Date(minTime); d.getTime() <= maxTime; d = addDays(d, 1)) {
    const dateKey = format(d, 'YYYY-MM-DD');
    const point = {
      date: dateKey,
      label: format(d, 'MMM DD, YYYY'),
      month: `${getMonth(d)}_${getYear(d)}`,
      isWeekend: isWeekend(d),
      isPoint: true,
    };

    statIds.forEach(id => {
      point[id] = byStat[id]?.get(dateKey) ?? 0;
    });

    if (!point.isWeekend) {
      points.push(point);
    }
  }

  return points;
};

const groupByMonth = (statistics, dateRange) => {
  const points = buildDailyPoints(statistics, dateRange);
  return points.reduce((acc, point) => {
    const monthKey = point.month;
    if (!acc[monthKey]) acc[monthKey] = [];
    acc[monthKey].push(point);
    return acc;
  }, {});
};

const aggregateMonth = points => {
  if (!points || points.length === 0) return {};

  const firstPoint = points[0];

  const aggregated = {
    label: format(parse(firstPoint.date), isThisYear(parse(firstPoint.date)) ? 'MMMM' : 'MMMM, YYYY'),
    date: parse(firstPoint.date),
    byDay: points,
  };

  const hourMetrics = ['physicianScheduledHours', 'physicianBookedHours', 'physicianActualHours'];
  hourMetrics.forEach(metricKey => {
    const values = points.map(point => point[metricKey] ?? 0);
    aggregated[metricKey] = values.reduce((sum, val) => sum + val, 0);
  });

  const totalScheduledHours = aggregated.physicianScheduledHours || 0;
  const totalBookedHours = aggregated.physicianBookedHours || 0;
  const totalActualHours = aggregated.physicianActualHours || 0;

  aggregated.physicianRelativeUtilization = totalBookedHours > 0 ? (totalScheduledHours / totalBookedHours) * 100 : 0;

  aggregated.physicianActualUtilization = totalBookedHours > 0 ? (totalActualHours / totalBookedHours) * 100 : 0;

  // Allocated % should not be conditioned on "days with bookings only".
  // Estimate daily total capacity from observed (allocatedHours / allocatedPct) and
  // fill missing weekdays using the median observed daily capacity.
  const capacitySamples = points
    .map(point => {
      const pct = Number(point.physicianBookedUtilization ?? 0);
      const hrs = Number(point.physicianBookedHours ?? 0);
      if (pct > 0 && hrs > 0) {
        return hrs / (pct / 100);
      }
      return null;
    })
    .filter(v => Number.isFinite(v) && v > 0);

  const assumedDailyCapacity = median(capacitySamples);

  const totalCapacityHours = points.reduce((sum, point) => {
    const pct = Number(point.physicianBookedUtilization ?? 0);
    const hrs = Number(point.physicianBookedHours ?? 0);
    if (pct > 0 && hrs > 0) {
      return sum + hrs / (pct / 100);
    }
    return sum + assumedDailyCapacity;
  }, 0);

  aggregated.physicianBookedUtilization = totalCapacityHours > 0 ? (totalBookedHours / totalCapacityHours) * 100 : 0;

  // Keep backward compatibility for old field names if they exist
  if (points[0].scheduledPatientsHours !== undefined) {
    const oldData = points.reduce(
      (acc, curr) => ({
        scheduledPatientsHours: (acc.scheduledPatientsHours || 0) + (curr.scheduledPatientsHours || 0),
        operationRoomBookingsHours: (acc.operationRoomBookingsHours || 0) + (curr.operationRoomBookingsHours || 0),
        actualUtilizedHours: (acc.actualUtilizedHours || 0) + (curr.actualUtilizedHours || 0),
        actualUtilization: curr.actualUtilization || 0, // Take last value for percentages
      }),
      {}
    );

    Object.assign(aggregated, oldData);
  }

  return aggregated;
};

const DetailTopBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  position: sticky;
  top: 0;
  z-index: 2;
`;

const DetailHeaderLayout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const DetailHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 2px;
`;

const DetailHeaderTitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const BackToPhysiciansButton = styled(Button)`
  && {
    white-space: nowrap;
  }
`;

const ViewingTitle = styled(Typography)`
  && {
    font-weight: 800;
    line-height: 1.1;
  }
`;

const RangeLabel = styled(Typography)`
  && {
    opacity: 0.75;
  }
`;

const SpecialtyChip = styled(Chip)`
  && {
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const formatLocalDateMonthYear = localDate => {
  if (!localDate) return '';
  try {
    const d = new Date(localDate.year(), localDate.monthValue() - 1, localDate.dayOfMonth());
    return format(d, 'MMM YYYY');
  } catch (e) {
    return '';
  }
};

const DetailHeader = ({ physician, dateRange, onBack }) => {
  const physicianName = typeof physician === 'object' ? physician?.name : undefined;
  const speciality = typeof physician === 'object' ? physician?.speciality : undefined;
  const rangeLabel = useMemo(() => {
    const fromLabel = formatLocalDateMonthYear(dateRange?.from);
    const toLabel = formatLocalDateMonthYear(dateRange?.to);
    if (fromLabel && toLabel) return `${fromLabel} – ${toLabel}`;
    return '';
  }, [dateRange?.from, dateRange?.to]);

  return (
    <DetailTopBar>
      <DetailHeaderLayout>
        <BackToPhysiciansButton startIcon={<ArrowBack />} onClick={onBack}>
          Back to Physicians
        </BackToPhysiciansButton>

        <DetailHeaderInfo>
          <DetailHeaderTitleRow>
            <ViewingTitle variant="h5">
              {physicianName ? `Viewing Dr ${physicianName}` : 'Viewing Physician'}
            </ViewingTitle>
            {speciality ? <SpecialtyChip size="small" label={speciality} /> : null}
          </DetailHeaderTitleRow>
          {rangeLabel ? <RangeLabel variant="body2">{rangeLabel}</RangeLabel> : null}
        </DetailHeaderInfo>
      </DetailHeaderLayout>
    </DetailTopBar>
  );
};

const PhysicianDetail = ({ statistics, physician, dateRange }) => {
  const byMonth = useMemo(() => groupByMonth(statistics ?? [], dateRange), [statistics, dateRange]);
  const aggregate = useMemo(() => Object.values(byMonth).map(aggregateMonth), [byMonth]);

  const exportFileName = useMemo(() => {
    const physicianName = typeof physician === 'object' ? physician?.name : undefined;
    if (physicianName) {
      return `Dr ${physicianName} Block Analytics Data`;
    }
    return 'Physician Block Analytics Data';
  }, [physician]);

  return (
    <CollectionList
      idProvider={item => item.id}
      entityName={'Physician'}
      tableKey={'single-physician-performance'}
      defaultSort={{ title: 'Month', type: 'DESC' }}
      columns={viewColumns}
      data={{ data: aggregate }}
      getList={payload => payload.data}
      useColumnSelection
      useCSVExport
      customExportCSV={exportPhysicianCSV}
      customExportXLSX={exportPhysicianXLSX}
      customExportPDF={exportPhysicianPDF}
      exportFileName={exportFileName}
      TitleAndActions={() => null}
      history={{}}
      location={{}}
    />
  );
};

const PhysicianList = ({ filter, setPhysician }) => {
  const exportFileName = useMemo(() => {
    if (filter?.dateRange?.from) {
      try {
        const localDate = filter.dateRange.from;
        const date = new Date(localDate.year(), localDate.monthValue() - 1, localDate.dayOfMonth());
        const monthYear = format(date, 'MMMM YYYY');
        return `${monthYear} Active Physicians Data`;
      } catch (e) {
        console.error('Error formatting export filename:', e);
        return 'Active Physicians Data';
      }
    }
    return 'Active Physicians Data';
  }, [filter]);

  return (
    <Subscription subscription={subscription} variables={{ filter }}>
      {data => (
        <CollectionList
          idProvider={item => item.id}
          entityName={'Physicians'}
          tableKey={'physician-performance'}
          defaultSort={{ title: 'Active Physicians', type: 'ASC' }}
          columns={listColumns}
          data={data}
          getList={payload => payload?.data?.physicians}
          setList={(payload, data) => {
            if (payload?.data) {
              payload.data.physicians = data;
            }
            return payload;
          }}
          useColumnSelection
          useCSVExport
          customExportCSV={exportPhysicianCSV}
          customExportXLSX={exportPhysicianXLSX}
          customExportPDF={exportPhysicianPDF}
          exportFileName={exportFileName}
          TitleAndActions={() => null}
          history={{}}
          location={{}}
          onViewClick={data => setPhysician(data?.physician)}
        />
      )}
    </Subscription>
  );
};

export default ({ physician, setPhysician, filter }) => {
  if (!physician) {
    return <PhysicianList filter={filter} setPhysician={setPhysician} />;
  }

  const monthFrom = filter?.dateRange?.from;
  const monthTo = filter?.dateRange?.to;

  // Keep the detail view showing all months (rolling year) while still allowing
  // the selected month (from the list) to match exactly.
  //
  // We anchor the 12-month window to the selected month.
  const detailDateRange =
    monthFrom && monthTo
      ? {
          from: monthFrom.withDayOfMonth(1).minusMonths(11),
          to: monthTo,
        }
      : undefined;

  return (
    <Subscription
      subscription={physicianStats}
      variables={{
        filter: {
          ...(filter || {}),
          physician: typeof physician === 'object' ? physician.id : physician,
          dateRange: detailDateRange,
        },
      }}
      skip={!detailDateRange?.from || !detailDateRange?.to}
    >
      {data => (
        <>
          <DetailHeader physician={physician} dateRange={detailDateRange} onBack={() => setPhysician(undefined)} />
          <PhysicianDetail
            statistics={data?.data?.physicianStats ?? []}
            physician={physician}
            dateRange={detailDateRange}
          />
        </>
      )}
    </Subscription>
  );
};
